import React from "react";
import { DataComponent, DataObserveKey, VisualComponent, H3, cn } from "@siteimprove/fancylib";
import * as scss from "./form.scss";

export interface FormControl<T> {
	/** Name applied to the form control */
	name?: string;
	/** Id applied to the form control */
	id?: string;
	/** Is the form control invalid */
	invalid?: boolean;
	/** Value of the form control */
	value: T;
	/** Callback for onChange event */
	onChange: (value: T) => void;
	/** Callback for onBlur event */
	onBlur?: React.FocusEventHandler;
	/** Label of the form control */
	"aria-label"?: string;
	/** ID of an an element that describes what the form control is for */
	"aria-describedby"?: string;
	/** ID of an an element that labels this form control */
	"aria-labelledby"?: string;
}

type FormProps = {
	/** Id applied to the form */
	id?: string;
	/** Elements to be displayed inside the form */
	children?: React.ReactNode;
	/** The heading for the form */
	heading?: string;
	/** Is the form in an invalid state */
	invalid?: boolean;
	/** Error descriptions for describing the invalid state of the form */
	errors?: string[];
	/** Should the error descriptions be hidden */
	hideErrorList?: boolean;
	/** Form styling options */
	variant?: "regular" | "slim" | "horizontal";
	/** Callback for onSubmit event */
	onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
} & DataComponent &
	VisualComponent &
	DataObserveKey;

export const Form = (props: FormProps): JSX.Element => {
	const {
		id,
		onSubmit,
		children,
		heading,
		invalid,
		errors,
		hideErrorList,
		className,
		style,
		variant = "regular",
	} = props;

	return (
		<form
			data-component={props["data-component"] || "form"}
			data-observe-key={props["data-observe-key"]}
			onSubmit={onSubmit}
			className={cn(scss.form, className, scss[variant])}
			style={style}
			id={id}
		>
			{heading && <FormHeading horizontal={variant === "horizontal"}>{heading}</FormHeading>}
			<div className={scss.container}>
				{variant === "horizontal"
					? React.Children.toArray(children).map(
							(e, i) =>
								React.isValidElement(e) &&
								React.cloneElement(e, { key: i, horizontal: true } as Partial<FormControl<unknown>>)
					  )
					: children}
			</div>
			<div role="alert" aria-relevant="additions" aria-atomic="true">
				{!hideErrorList && invalid && errors && (
					<ul className={scss.errorsContainer}>
						{errors.map((error, index) => {
							return (
								<li className={scss.validationError} key={index}>
									{error}
								</li>
							);
						})}
					</ul>
				)}
			</div>
		</form>
	);
};

function FormHeading({ children, horizontal }: { horizontal: boolean; children: string }) {
	const heading = <H3 lineHeight="multi-line">{children}</H3>;
	if (horizontal) {
		return (
			<div className={scss.formHeading}>
				<div />
				<div>{heading}</div>
			</div>
		);
	} else {
		return <div className={scss.formHeading}>{heading}</div>;
	}
}
